import { css } from '@emotion/react';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import t from 'react-translate';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import AthenaTextInput, { AthenaTextInputType } from 'athena/components/text-input';
import NvDropdown, { NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { gray2, gray7 } from 'styles/global_defaults/colors';
import { config } from '@config/pendo.config.json';
import { TargetAudience } from 'redux/schemas/models/mentoring-programs';


// The dropdown option values don't match the MentoringProgramGuideRequirementType enum exactly,
// so we create a separate enum for the option values
export enum AppearsInOptionValues {
  MY_GOALS = 'MY_GOALS',
  PROFILE_COMPLETION = 'PROFILE_COMPLETION',
  SPECIFIC_SESSION = 'SPECIFIC_SESSION',
  RANGE_OF_SESSIONS = 'RANGE_OF_SESSIONS',
}

const DeliverySection = () => {
  const translationBase = t.MENTORING_PROGRAMS.GUIDANCE.GUIDES;

  const styles = css`
    i.icon-sm {
      color: ${gray2};
      background-color: ${gray7};
      padding: 4px;
      border-radius: 50%;

      &.icon-fullscreen {
        transform: rotate(45deg);
      }
    }
  `;

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const mentoringProgramAliases = useSelector((state) => getMentoringProgramAliases(state, mentoringProgram.id));

  const { watch, setValue, getValues } = useFormContext();
  const appearsIn = watch('appearsIn') as AppearsInOptionValues;
  const audience = watch('audience') as TargetAudience;

  const handleSequenceBlur = () => {
    const lowerLimit = getValues('sequenceLowerLimit');
    const upperLimit = getValues('sequenceUpperLimit');

    if (lowerLimit && upperLimit && parseInt(lowerLimit, 10) > parseInt(upperLimit, 10)) {
      setValue('sequenceLowerLimit', upperLimit, { shouldValidate: true });
      setValue('sequenceUpperLimit', lowerLimit, { shouldValidate: true });
    }
  };

  const audienceOptions: NvDropdownOption[] = [
    {
      type: 'text',
      text: translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.MENTORS_AND_MENTEES(mentoringProgramAliases),
      callback: () => setValue('audience', TargetAudience.MENTOR_AND_MENTEE, { shouldValidate: true }),
      value: TargetAudience.MENTOR_AND_MENTEE,
      dataQa: config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.roleDropdown.mentorAndMentee,
    },
    {
      type: 'text',
      text: mentoringProgramAliases.MentorsAlias,
      callback: () => setValue('audience', TargetAudience.MENTOR, { shouldValidate: true }),
      value: TargetAudience.MENTOR,
      dataQa: config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.roleDropdown.mentorOnly,
    },
    {
      type: 'text',
      text: mentoringProgramAliases.MenteesAlias,
      callback: () => setValue('audience', TargetAudience.MENTEE, { shouldValidate: true }),
      value: TargetAudience.MENTEE,
      dataQa: config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.roleDropdown.menteeOnly,
    },
  ];

  const appearsInOptions: NvDropdownOption[] = [
    {
      type: 'text',
      text: translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.MY_GOALS(),
      iconClass: 'goal',
      callback: () => setValue('appearsIn', AppearsInOptionValues.MY_GOALS, { shouldValidate: true }),
      value: AppearsInOptionValues.MY_GOALS,
      dataQa: config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.criteriaTypeDropdown.myGoals,
    },
    {
      type: 'text',
      text: translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.PROFILE_COMPLETION(),
      iconClass: 'enrollment',
      callback: () => setValue('appearsIn', AppearsInOptionValues.PROFILE_COMPLETION, { shouldValidate: true }),
      value: AppearsInOptionValues.PROFILE_COMPLETION,
      dataQa: config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.criteriaTypeDropdown.profileCompletion,
    },
    {
      type: 'text',
      text: translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.A_SPECIFIC_SESSION(),
      iconClass: 'calendar',
      callback: () => setValue('appearsIn', AppearsInOptionValues.SPECIFIC_SESSION, { shouldValidate: true }),
      value: AppearsInOptionValues.SPECIFIC_SESSION,
      dataQa: config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.criteriaTypeDropdown.specificSession,
    },
    {
      type: 'text',
      text: translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.A_RANGE_OF_SESSIONS(),
      iconClass: 'fullscreen',
      callback: () => setValue('appearsIn', AppearsInOptionValues.RANGE_OF_SESSIONS, { shouldValidate: true }),
      value: AppearsInOptionValues.RANGE_OF_SESSIONS,
      dataQa: config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.criteriaTypeDropdown.sessionRange,
    },
  ];

  return (
    <div css={styles} className='bg-color-info border-radius-2 p-4 mt-5'>
      <div>
        <label className='color-gray-2 semi-bold font-size-medium mb-1'>
          {translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.AUDIENCE()}
          <span className='color-danger'>*</span>
        </label>
        <NvDropdown
          items={audienceOptions}
          buttonStyle={NvDropdownButtonStyle.FORM_SMALL}
          showSelectedIndicator
          menuClassName='w-100'
          initialIndex={audienceOptions.findIndex((option) => (option as any).value === audience)}
          // `option` needs to be typecast as `any` because not all NvDropdownOptions have a `value` property
          // All of the items are NvDropdownTextItems, which do have a `value` property, so this is safe
          // TODO: determine if I should change the type of the options list to NvDropdownTextItems[],
          // or if I should update the other NvDropdownOptions to include an optional `value` property
          toggleDataQa={config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.roleDropdown.toggle}
        />
      </div>
      <div className='mt-2'>
        <label className='color-gray-2 semi-bold font-size-medium mb-1'>
          {translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.APPEARS_IN()}
          <span className='color-danger'>*</span>
        </label>
        <div className='d-flex align-items-center'>
          <div className={appearsIn === AppearsInOptionValues.SPECIFIC_SESSION || appearsIn === AppearsInOptionValues.RANGE_OF_SESSIONS ? 'w-50' : 'w-100'}>
            <NvDropdown
              items={appearsInOptions}
              buttonStyle={NvDropdownButtonStyle.FORM_SMALL}
              showSelectedIndicator
              menuClassName='w-100'
              drop='down'
              initialIndex={appearsInOptions.findIndex((option) => (option as any).value === appearsIn)}
              // Same note as above regarding typecasting to `any`
              toggleDataQa={config.pendo.athena.mentorshipProgram.guides.addGuideFlyout.criteriaTypeDropdown.toggle}
            />
          </div>
          {appearsIn === AppearsInOptionValues.SPECIFIC_SESSION && (
            <div className='d-flex align-items-center ml-2 w-50'>
              <AthenaTextInput
                withForm
                name='upperLimit'
                type={AthenaTextInputType.NUMBER}
                min={0}
                className='w-100 ml-2'
                showLabel={false}
              />
            </div>
          )}
          {appearsIn === AppearsInOptionValues.RANGE_OF_SESSIONS && (
            <div className='d-flex align-items-center ml-2 w-50'>
              <AthenaTextInput
                withForm
                name='sequenceLowerLimit'
                type={AthenaTextInputType.NUMBER}
                min={0}
                showLabel={false}
                onBlur={handleSequenceBlur}
              />
              <span className='mx-2'>
                {translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.TO()}
              </span>
              <AthenaTextInput
                withForm
                name='sequenceUpperLimit'
                type={AthenaTextInputType.NUMBER}
                min={0}
                showLabel={false}
                onBlur={handleSequenceBlur}
              />
            </div>
          )}
        </div>
      </div>
      {appearsIn === AppearsInOptionValues.MY_GOALS && <div className='mt-2 text-small color-gray-2'>{translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.MY_GOALS_HELP_TEXT()}</div>}
      {appearsIn === AppearsInOptionValues.PROFILE_COMPLETION && <div className='mt-2 text-small color-gray-2'>{translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.PROFILE_COMPLETION_HELP_TEXT()}</div>}
      {appearsIn === AppearsInOptionValues.SPECIFIC_SESSION && <div className='mt-2 text-small color-gray-2'>{translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.A_SPECIFIC_SESSION_HELP_TEXT()}</div>}
      {appearsIn === AppearsInOptionValues.RANGE_OF_SESSIONS && <div className='mt-2 text-small color-gray-2'>{translationBase.CREATE_GUIDE_FLYOUT.DELIVERY_SECTION.A_RANGE_OF_SESSIONS_HELP_TEXT()}</div>}
    </div>
  );
};

export default DeliverySection;
